import {
  BandLayer,
  ChartFrame,
  DiffArea,
  Legend,
  LineLayer,
  valueDomain,
  ZeroLine,
  type ChartEvent,
} from '@finsim/charts';
import type { Scenario } from '@finsim/model';
import { useUnit } from 'effector-react';
import { Copy, GitCompareArrows } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { McMetric, ScenarioResult } from '../../../entities/simulation';
import { duplicateVariantRequested, setBaselineRequested } from '../../../features/manage-variants';
import { cn } from '../../../shared/lib/cn';
import { Button, EmptyState, Field, Segmented, Select } from '../../../shared/ui';
import { difference, diffStory, type DiffStory } from '../lib/compare';
import { figureDelta, formatDelta, keyFigureRows } from '../lib/figures';
import { scenarioColor } from '../lib/series';
import { ChartCard } from './chart-card';
import { FanChart } from './fan-chart';
import { useFanTable } from './fan-table';
import { SeriesChart } from './series-chart';
import type { CanvasData } from './use-canvas';

interface Comparison {
  scenario: Scenario;
  /** Net worth minus the baseline's (deterministic, current value mode). */
  diff: Float64Array;
  story: DiffStory;
}

/**
 * Compare view (M6): every variant in the compare set against the baseline — a plain-language
 * summary, an overlay, the difference over time, the chance of being ahead (Monte Carlo) and a
 * table of key figures.
 */
export function Compare({ data }: { data: CanvasData }) {
  const { t } = useTranslation();
  const duplicate = useUnit(duplicateVariantRequested);
  const { project, result, visible, valueMode } = data;
  const baseline = project.scenarios.find(
    (s) => s.id === project.baselineScenarioId && result.scenarios[s.id] !== undefined,
  );
  const comparisons = useMemo<Comparison[]>(() => {
    const base = baseline ? result.scenarios[baseline.id]?.series.netWorth[valueMode] : undefined;
    if (!baseline || !base) return [];
    return visible.flatMap((scenario) => {
      const own = result.scenarios[scenario.id]?.series.netWorth[valueMode];
      if (scenario.id === baseline.id || !own) return [];
      const diff = difference(own, base);
      return [{ scenario, diff, story: diffStory(diff) }];
    });
  }, [baseline, visible, result, valueMode]);

  if (!baseline || comparisons.length === 0) {
    const source = data.focused ?? baseline;
    return (
      <EmptyState
        icon={<GitCompareArrows />}
        title={t('compare.emptyTitle')}
        body={t('compare.emptyBody')}
        action={
          source && (
            <Button
              variant="primary"
              leadingIcon={<Copy />}
              onClick={() => {
                duplicate({
                  scenarioId: source.id,
                  mode: 'share',
                  name: t('variants.copyName', { name: source.name }),
                });
              }}
            >
              {t('compare.duplicate', { name: source.name })}
            </Button>
          )
        }
        className="flex-1 py-24"
      />
    );
  }
  return <CompareContent data={data} baseline={baseline} comparisons={comparisons} />;
}

function CompareContent({
  data,
  baseline,
  comparisons,
}: {
  data: CanvasData;
  baseline: Scenario;
  comparisons: Comparison[];
}) {
  const { t } = useTranslation();
  const setBaseline = useUnit(setBaselineRequested);
  const compared = useMemo(
    () => [baseline, ...comparisons.map((c) => c.scenario)],
    [baseline, comparisons],
  );

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Field label={t('compare.baseline')} className="w-72">
          {({ id }) => (
            <Select
              id={id}
              value={baseline.id}
              options={data.scenarios.map((s) => ({ value: s.id, label: s.name }))}
              onValueChange={(scenarioId) => {
                setBaseline({ scenarioId });
              }}
            />
          )}
        </Field>
        <Legend
          label={t('charts.legend')}
          items={compared.map((s) => ({ id: s.id, label: s.name, color: scenarioColor(s) }))}
        />
      </div>
      <StoryCard data={data} baseline={baseline} comparisons={comparisons} />
      <OverlayCard data={data} compared={compared} />
      <DifferenceCard data={data} baseline={baseline} comparisons={comparisons} />
      {data.mc && <BeatsCard data={data} baseline={baseline} comparisons={comparisons} />}
      <FiguresCard data={data} baseline={baseline} compared={compared} />
    </div>
  );
}

/** One or two sentences per variant: where it ends and when it moves ahead or falls behind. */
function StoryCard({
  data,
  baseline,
  comparisons,
}: {
  data: CanvasData;
  baseline: Scenario;
  comparisons: Comparison[];
}) {
  const { t } = useTranslation();
  const { dates, fmt, mc } = data;
  const last = dates.length - 1;
  const year = dates[last] ? fmt.year(dates[last]) : '';

  return (
    <section
      aria-label={t('compare.storyTitle')}
      className="space-y-2 rounded-lg border border-border-subtle bg-bg-1 px-4 py-3"
    >
      <h2 className="text-sm font-medium">{t('compare.storyTitle')}</h2>
      {comparisons.map(({ scenario, story }) => {
        const params = {
          name: scenario.name,
          baseline: baseline.name,
          amount: fmt.money(Math.abs(story.final)),
          year,
        };
        const crossDate = story.crossover === null ? undefined : dates[story.crossover];
        const when =
          story.outcome === 'level'
            ? null
            : crossDate
              ? t(story.outcome === 'ahead' ? 'compare.overtakes' : 'compare.fallsBehind', {
                  date: fmt.monthLong(crossDate),
                })
              : t(
                  story.outcome === 'ahead'
                    ? 'compare.throughoutAhead'
                    : 'compare.throughoutBehind',
                );
        const mcScenario = mc?.scenarios[scenario.id];
        const diffBands = mcScenario?.diffBaseline?.[data.valueMode];
        return (
          <div key={scenario.id} className="flex gap-2.5 text-sm">
            <span
              aria-hidden
              className="mt-1.5 size-2 shrink-0 rounded-full"
              style={{ background: scenarioColor(scenario) }}
            />
            <p className="text-text-2">
              <span className="text-text-1">{t(`compare.${story.outcome}`, params)}</span> {when}
              {mcScenario?.pBeatsBaseline && diffBands && (
                <span className="block text-text-3">
                  {t('compare.mcBeats', {
                    chance: fmt.probability(mcScenario.pBeatsBaseline[last] ?? 0),
                    year,
                    median: fmt.signed(diffBands.p50[last] ?? 0),
                    from: fmt.compact(diffBands.p10[last] ?? 0),
                    to: fmt.compact(diffBands.p90[last] ?? 0),
                  })}
                </span>
              )}
            </p>
          </div>
        );
      })}
    </section>
  );
}

/** Net worth or cash of every compared variant on one chart (fans in Monte Carlo mode). */
function OverlayCard({ data, compared }: { data: CanvasData; compared: Scenario[] }) {
  const { t } = useTranslation();
  const [metric, setMetric] = useState<McMetric>('netWorth');
  const [hover, setHover] = useState(-1);
  const metricLabel = metric === 'netWorth' ? t('charts.netWorth') : t('charts.cash');
  const title = t('compare.overlayTitle', { metric: metricLabel });
  const fanTable = useFanTable(data, metric, compared, title);
  const scoped = { ...data, visible: compared };
  const select = useCallback((r: ScenarioResult) => r.series[metric], [metric]);

  return (
    <ChartCard
      title={title}
      subtitle={[data.mc ? t('mc.bandsCaption') : undefined, data.moneyCaption]
        .filter(Boolean)
        .join(' · ')}
      table={
        data.mc
          ? fanTable
          : {
              caption: title,
              columns: [t('charts.year'), ...compared.map((s) => s.name)],
              rows: data.buckets.map((b) => ({
                label: String(b.year),
                values: compared.map((s) =>
                  data.fmt.money(
                    data.result.scenarios[s.id]?.series[metric][data.valueMode][b.to] ?? 0,
                  ),
                ),
              })),
            }
      }
      actions={
        <Segmented
          label={t('risk.metric')}
          value={metric}
          onValueChange={setMetric}
          options={[
            { value: 'netWorth', label: t('charts.netWorth') },
            { value: 'cash', label: t('charts.cash') },
          ]}
        />
      }
    >
      {data.mc ? (
        <FanChart
          data={scoped}
          title={title}
          metric={metric}
          scenarios={compared}
          height={260}
          hover={hover}
          onHover={setHover}
        />
      ) : (
        <SeriesChart
          data={scoped}
          title={title}
          select={select}
          height={260}
          hover={hover}
          onHover={setHover}
        />
      )}
    </ChartCard>
  );
}

/** Each variant's net worth minus the baseline's, with its crossover month marked. */
function DifferenceCard({
  data,
  baseline,
  comparisons,
}: {
  data: CanvasData;
  baseline: Scenario;
  comparisons: Comparison[];
}) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(-1);
  const { mc, valueMode, dates, range, buckets, fmt } = data;
  const lines = comparisons.map((c) => {
    const bands = mc?.scenarios[c.scenario.id]?.diffBaseline?.[valueMode];
    return { ...c, bands, values: bands ? bands.p50 : c.diff };
  });
  const domain = valueDomain(
    lines.flatMap((l) => (l.bands ? [l.bands.p10, l.bands.p90] : [l.values])),
    { range },
  );
  const events: ChartEvent[] = lines.flatMap((l) => {
    const story = l.bands ? diffStory(l.bands.p50) : l.story;
    if (story.crossover === null) return [];
    return [
      {
        index: story.crossover,
        label: t(story.outcome === 'ahead' ? 'compare.movesAhead' : 'compare.fallsBehindEvent', {
          name: l.scenario.name,
        }),
        tone: story.outcome === 'ahead' ? 'positive' : 'critical',
      },
    ];
  });
  const title = t('compare.diffTitle', { baseline: baseline.name });

  return (
    <ChartCard
      title={title}
      subtitle={[
        t('compare.diffCaption'),
        mc ? `${t('mc.median')}, ${t('mc.inner')}` : undefined,
        data.moneyCaption,
      ]
        .filter(Boolean)
        .join(' · ')}
      table={{
        caption: title,
        columns: [t('charts.year'), ...lines.map((l) => l.scenario.name)],
        rows: buckets.map((b) => ({
          label: String(b.year),
          values: lines.map((l) => fmt.signed(l.values[b.to] ?? 0)),
        })),
      }}
    >
      <ChartFrame
        markers={data.markers}
        dates={dates}
        range={range}
        height={220}
        yDomain={domain}
        formatY={fmt.compact}
        formatXTick={fmt.year}
        ariaLabel={t('compare.diffAria', { baseline: baseline.name })}
        hoverIndex={hover}
        onHoverChange={setHover}
        events={events}
        renderTooltip={(i) => (
          <div className="space-y-1">
            <p className="text-text-3">{dates[i] ? fmt.monthLong(dates[i]) : ''}</p>
            {lines.map((l) => (
              <p key={l.scenario.id} className="flex items-center justify-between gap-4">
                <span className="flex items-center gap-1.5 text-text-2">
                  <span
                    aria-hidden
                    className="h-0.5 w-2.5 rounded-full"
                    style={{ background: scenarioColor(l.scenario) }}
                  />
                  {l.scenario.name}
                </span>
                <span
                  className={cn(
                    'tabular font-medium',
                    (l.values[i] ?? 0) < 0 ? 'text-negative' : 'text-positive',
                  )}
                >
                  {fmt.signed(l.values[i] ?? 0)}
                </span>
              </p>
            ))}
          </div>
        )}
      >
        {lines.length === 1 && lines[0] && !lines[0].bands && <DiffArea values={lines[0].values} />}
        {lines.map(
          (l) =>
            l.bands && (
              <BandLayer
                key={`band-${l.scenario.id}`}
                lower={l.bands.p10}
                upper={l.bands.p90}
                color={scenarioColor(l.scenario)}
                opacity={0.16}
              />
            ),
        )}
        <ZeroLine />
        {lines.map((l) => (
          <LineLayer key={l.scenario.id} values={l.values} color={scenarioColor(l.scenario)} />
        ))}
      </ChartFrame>
    </ChartCard>
  );
}

/** Monte Carlo: share of paths on which each variant is ahead of the baseline. */
function BeatsCard({
  data,
  baseline,
  comparisons,
}: {
  data: CanvasData;
  baseline: Scenario;
  comparisons: Comparison[];
}) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(-1);
  const { mc, dates, range, buckets, fmt } = data;
  const half = useMemo(() => new Float64Array(dates.length).fill(0.5), [dates.length]);
  const lines = comparisons.flatMap(({ scenario }) => {
    const values = mc?.scenarios[scenario.id]?.pBeatsBaseline;
    return values ? [{ scenario, values }] : [];
  });
  const title = t('compare.pBeatsTitle', { baseline: baseline.name });

  return (
    <ChartCard
      title={title}
      table={{
        caption: title,
        columns: [t('charts.year'), ...lines.map((l) => l.scenario.name)],
        rows: buckets.map((b) => ({
          label: String(b.year),
          values: lines.map((l) => fmt.probability(l.values[b.to] ?? 0)),
        })),
      }}
    >
      <ChartFrame
        markers={data.markers}
        dates={dates}
        range={range}
        height={180}
        yDomain={[0, 1]}
        formatY={fmt.percent}
        formatXTick={fmt.year}
        ariaLabel={t('compare.pBeatsAria', { baseline: baseline.name })}
        hoverIndex={hover}
        onHoverChange={setHover}
        renderTooltip={(i) => (
          <div className="space-y-1">
            <p className="text-text-3">{dates[i] ? fmt.monthLong(dates[i]) : ''}</p>
            {lines.map((l) => (
              <p key={l.scenario.id} className="flex items-center justify-between gap-4">
                <span className="flex items-center gap-1.5 text-text-2">
                  <span
                    aria-hidden
                    className="h-0.5 w-2.5 rounded-full"
                    style={{ background: scenarioColor(l.scenario) }}
                  />
                  {l.scenario.name}
                </span>
                <span className="tabular font-medium">{fmt.probability(l.values[i] ?? 0)}</span>
              </p>
            ))}
          </div>
        )}
      >
        <LineLayer values={half} color="var(--chart-axis)" width={1} dashed />
        {lines.map((l) => (
          <LineLayer key={l.scenario.id} values={l.values} color={scenarioColor(l.scenario)} />
        ))}
      </ChartFrame>
    </ChartCard>
  );
}

/** Key figures side by side, with the difference from the baseline under each value. */
function FiguresCard({
  data,
  baseline,
  compared,
}: {
  data: CanvasData;
  baseline: Scenario;
  compared: Scenario[];
}) {
  const { t } = useTranslation();
  const rows = keyFigureRows({ ...data, t }, baseline);

  return (
    <ChartCard title={t('compare.metricsTitle')} subtitle={data.moneyCaption}>
      <div className="overflow-x-auto pb-2">
        <table className="w-full border-collapse text-xs">
          <caption className="sr-only">{t('compare.metricsTitle')}</caption>
          <thead>
            <tr>
              <th
                scope="col"
                className="border-b border-border-subtle px-2 py-1.5 text-left font-medium text-text-3"
              >
                {t('compare.figure')}
              </th>
              {compared.map((s) => (
                <th
                  key={s.id}
                  scope="col"
                  className="border-b border-border-subtle px-2 py-1.5 text-right font-medium text-text-2"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      aria-hidden
                      className="size-2 rounded-full"
                      style={{ background: scenarioColor(s) }}
                    />
                    {s.name}
                    {s.id === baseline.id && (
                      <span className="font-normal text-text-3">({t('compare.baseline')})</span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              return (
                <tr key={row.key} className="hover:bg-bg-3">
                  <th scope="row" className="px-2 py-1.5 text-left font-normal text-text-2">
                    {row.label}
                  </th>
                  {compared.map((s) => {
                    const v = row.value(s);
                    const change = figureDelta(row, s, baseline);
                    return (
                      <td key={s.id} className="tabular px-2 py-1.5 text-right align-top">
                        <span className="block text-text-1">
                          {row.text ? row.text(s) : v === null ? '–' : row.format(v)}
                        </span>
                        {change && (
                          <span
                            className={cn(
                              'block',
                              change.good === null
                                ? 'text-text-3'
                                : change.good
                                  ? 'text-positive'
                                  : 'text-negative',
                            )}
                          >
                            {t('compare.vsBaseline', {
                              delta: formatDelta(row, change.delta, data.fmt),
                            })}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
}
